import { api, ApiTags } from './base';
import type {
  CreateAgroDocumentMutation,
  CreateCessionDocumentMutation,
  CreateDocumentMutation,
  CreateLicenseDocumentMutation,
  CreateOperationalRequestMutation,
  CreateSupplyDocumentMutation,
  GetAllControlCardsQuery,
  GetAllOperationalRequestsQuery,
  GetAllShipmentRegistriesQuery,
  GetAllShipmentReportsQuery,
  GetAllShipmentTasksQuery,
  GetCoreDocumentsListQuery,
  GetCoreDocumentsQuery,
  GetOneControlCardQuery,
  GetOneControlCardQueryVariables,
  GetOneOperationalRequestQuery,
  GetOneOperationalRequestQueryVariables,
  GetOneShipmentRegistryQuery,
  GetOneShipmentRegistryQueryVariables,
  GetOneShipmentReportQuery,
  GetOneShipmentReportQueryVariables,
  GetOneShipmentTaskQuery,
  GetOneShipmentTaskQueryVariables,
  RevokeControlCardsMutationVariables,
  RevokeOperationalRequestMutationVariables,
  RevokeShipmentTasksMutationVariables,
  SendToCorrectControlCardMutationVariables,
  SendToCorrectOperationalRequestMutationVariables,
  SendToCorrectShipmentTaskMutationVariables,
  SignDocumentMutationVariables,
  UpdateControlCardMutation,
  UpdateControlCardMutationVariables,
  UpdateDocumentMutation,
  UpdateShipmentReportMutationVariables,
  UpdateShipmentTaskMutation,
  UpdateShipmentTaskMutationVariables,
} from './generated';
import './generated';

type TagId = string | number | null | undefined;

const ids = (value?: TagId[] | TagId) => {
  const list = value == null ? [] : Array.isArray(value) ? value : [value];
  return list.filter((id): id is string | number => id != null && `${id}`.length > 0).map(String);
};

const documentItems = (value?: TagId[] | TagId) =>
  ids(value).map((id) => ({ type: ApiTags.Document, id }));

const documentList = (rows?: Array<{ id?: string | null } | null> | null) => [
  { type: ApiTags.DocumentList, id: 'LIST' as const },
  ...documentItems(rows?.map((row) => row?.id)),
];

const invalidateDocuments = (value?: TagId[] | TagId) => [
  ...documentItems(value),
  { type: ApiTags.DocumentList, id: 'LIST' as const },
];

api.enhanceEndpoints({
  endpoints: {
    GetMe: { providesTags: [ApiTags.Profile] },
    GetCoreDictionariesCountries: { providesTags: [ApiTags.Dictionary] },
    GetCoreDictionariesBanks: { providesTags: [ApiTags.Dictionary] },
    GetPowersList: { providesTags: [ApiTags.StaffPowers] },
    GetMyPaymentOptions: { providesTags: [ApiTags.PaymentOptions] },
    GetMyOrganizationDocuments: { providesTags: [ApiTags.OrganizationDocumentList] },
    GetContractorOrganizationDocuments: { providesTags: [ApiTags.OrganizationDocumentList] },
    SearchPoaUsers: { providesTags: [ApiTags.PoaUserList] },
    StaffList: { providesTags: [ApiTags.StaffList] },
    StaffById: { providesTags: [ApiTags.Staff] },
    StaffIncomingInvites: { providesTags: [ApiTags.StaffInviteList] },
    StaffOutgoingInvites: { providesTags: [ApiTags.StaffInviteList] },
    StaffIncomingInvitesCount: { providesTags: [ApiTags.StaffInviteList] },
    StaffOutgoingInvitesCount: { providesTags: [ApiTags.StaffInviteList] },
    GetMyOrganizations: { providesTags: [ApiTags.Organization] },
    GetPartnersOrganizations: { providesTags: [ApiTags.OrganizationList] },
    GetMyIncomingInvitations: { providesTags: [ApiTags.OrganizationInvite] },
    GetMyOutgoingInvitations: { providesTags: [ApiTags.OrganizationInvite] },
    GetMyIncomingInvitationsCount: { providesTags: [ApiTags.OrganizationInvite] },
    GetMyOutgoingInvitationsCount: { providesTags: [ApiTags.OrganizationInvite] },
    GetPoaDraftsList: { providesTags: [ApiTags.PoaList] },
    GetPoaDraftsAndXml: { providesTags: [ApiTags.Poa] },

    GetCoreDocuments: {
      providesTags: (result: GetCoreDocumentsQuery | undefined) =>
        documentItems(result?.coreDocuments?.get?.map((item) => item.id)),
    },
    GetCoreDocumentsList: {
      providesTags: (result: GetCoreDocumentsListQuery | undefined) =>
        documentList(result?.coreDocuments?.query?.documents),
    },
    GetAllOperationalRequests: {
      providesTags: (result: GetAllOperationalRequestsQuery | undefined) =>
        documentList(result?.app5182dd71_OperationalRequest?.getList?.data),
    },
    GetAllShipmentTasks: {
      providesTags: (result: GetAllShipmentTasksQuery | undefined) =>
        documentList(result?.app5182dd71_ShipmentTask?.getList?.data),
    },
    GetAllControlCards: {
      providesTags: (result: GetAllControlCardsQuery | undefined) =>
        documentList(result?.app5182dd71_ControlCard?.getList?.data),
    },
    GetAllShipmentReports: {
      providesTags: (result: GetAllShipmentReportsQuery | undefined) =>
        documentList(result?.app5182dd71_ShipmentReport?.getList?.data),
    },
    GetAllShipmentRegistries: {
      providesTags: (result: GetAllShipmentRegistriesQuery | undefined) =>
        documentList(result?.app5182dd71_ShipmentRegistry?.getList?.data),
    },
    GetOneOperationalRequest: {
      providesTags: (
        result: GetOneOperationalRequestQuery | undefined,
        _error: unknown,
        arg: GetOneOperationalRequestQueryVariables,
      ) => documentItems(result?.app5182dd71_OperationalRequest?.getOne?.id ?? arg.documentId),
    },
    GetOneShipmentTask: {
      providesTags: (
        result: GetOneShipmentTaskQuery | undefined,
        _error: unknown,
        arg: GetOneShipmentTaskQueryVariables,
      ) => documentItems(result?.app5182dd71_ShipmentTask?.getOne?.id ?? arg.documentId),
    },
    GetOneControlCard: {
      providesTags: (
        result: GetOneControlCardQuery | undefined,
        _error: unknown,
        arg: GetOneControlCardQueryVariables,
      ) => documentItems(result?.app5182dd71_ControlCard?.getOne?.id ?? arg.documentId),
    },
    GetOneShipmentReport: {
      providesTags: (
        result: GetOneShipmentReportQuery | undefined,
        _error: unknown,
        arg: GetOneShipmentReportQueryVariables,
      ) => documentItems(result?.app5182dd71_ShipmentReport?.getOne?.id ?? arg.documentId),
    },
    GetOneShipmentRegistry: {
      providesTags: (
        result: GetOneShipmentRegistryQuery | undefined,
        _error: unknown,
        arg: GetOneShipmentRegistryQueryVariables,
      ) => documentItems(result?.app5182dd71_ShipmentRegistry?.getOne?.id ?? arg.documentId),
    },

    CreateDocument: {
      invalidatesTags: (result: CreateDocumentMutation | undefined) =>
        invalidateDocuments(result?.app70e9b8a9_Document?.create?.id),
    },
    CreateLicenseDocument: {
      invalidatesTags: (result: CreateLicenseDocumentMutation | undefined) =>
        invalidateDocuments(result?.app5705572d_Document?.create?.id),
    },
    CreateAgroDocument: {
      invalidatesTags: (result: CreateAgroDocumentMutation | undefined) =>
        invalidateDocuments(result?.app36c5da51_Document?.create?.id),
    },
    CreateSupplyDocument: {
      invalidatesTags: (result: CreateSupplyDocumentMutation | undefined) =>
        invalidateDocuments(result?.app12d801b5_Document?.create?.id),
    },
    CreateCessionDocument: {
      invalidatesTags: (result: CreateCessionDocumentMutation | undefined) =>
        invalidateDocuments(result?.app1d1b2841_Document?.create?.id),
    },
    CreateOperationalRequest: {
      invalidatesTags: (result: CreateOperationalRequestMutation | undefined) =>
        invalidateDocuments(result?.app5182dd71_OperationalRequest?.create?.id),
    },
    NewShipmentRegistry: {
      invalidatesTags: [{ type: ApiTags.DocumentList, id: 'LIST' }],
    },
    SignDocument: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: SignDocumentMutationVariables,
      ) => invalidateDocuments(arg.documentId),
    },
    UpdateDocument: {
      invalidatesTags: (result: UpdateDocumentMutation | undefined) =>
        invalidateDocuments(result?.app70e9b8a9_Document?.update?.id),
    },
    UpdateShipmentTask: {
      invalidatesTags: (
        result: UpdateShipmentTaskMutation | undefined,
        _error: unknown,
        arg: UpdateShipmentTaskMutationVariables,
      ) =>
        invalidateDocuments(result?.app5182dd71_ShipmentTask?.update?.id ?? arg.documentId),
    },
    UpdateControlCard: {
      invalidatesTags: (
        result: UpdateControlCardMutation | undefined,
        _error: unknown,
        arg: UpdateControlCardMutationVariables,
      ) =>
        invalidateDocuments(result?.app5182dd71_ControlCard?.update?.id ?? arg.documentId),
    },
    UpdateShipmentReport: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: UpdateShipmentReportMutationVariables,
      ) => invalidateDocuments(arg.documentId),
    },
    RevokeOperationalRequest: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: RevokeOperationalRequestMutationVariables,
      ) => invalidateDocuments(arg.documentIds),
    },
    RevokeShipmentTasks: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: RevokeShipmentTasksMutationVariables,
      ) => invalidateDocuments(arg.documentIds),
    },
    RevokeControlCards: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: RevokeControlCardsMutationVariables,
      ) => invalidateDocuments(arg.documentIds),
    },
    SendToCorrectOperationalRequest: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: SendToCorrectOperationalRequestMutationVariables,
      ) => invalidateDocuments(arg.documentId),
    },
    SendToCorrectShipmentTask: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: SendToCorrectShipmentTaskMutationVariables,
      ) => invalidateDocuments(arg.documentId),
    },
    SendToCorrectControlCard: {
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        arg: SendToCorrectControlCardMutationVariables,
      ) => invalidateDocuments(arg.documentId),
    },

    CreatePaymentOptions: { invalidatesTags: [ApiTags.PaymentOptions] },
    UpdatePaymentOptions: { invalidatesTags: [ApiTags.PaymentOptions] },
    SetPaymentOptions: { invalidatesTags: [ApiTags.PaymentOptions] },
  },
});
